import 'react-phone-number-input/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import styles from 'components/contactForm/contactForm.module.css';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, Form, Formik } from 'formik';
import emailjs from '@emailjs/browser';

import { useContactFormSchema } from 'components/contactForm/contactFormSchema';
import { CustomField } from 'components/contactFormCustomFields/CustomField';
import { Notification } from 'components/notifications/Notification';
import { StatusOkIcon } from 'components/vectors/notifications/StatusOkIcon';
import { StatusErrorIcon } from 'components/vectors/notifications/StatusErrorIcon';
import { useGetNotificationTextData } from 'components/contactForm/notification.hooks';
import { TFormData } from 'components/contactForm/contactForm.types';
import {
    useGetAddServicesOptions,
    useGetEventOptions,
} from 'components/contactForm/contactForm.hooks';
import { ContactFormFooter } from 'components/contactForm/ContactFormFooter';
import {
    EMAIL_PUBLIC_KEY,
    EMAIL_SERVICE_ID,
    EMAIL_TEMPLATE_ID_CALCULATION,
    WINDOW_DATALAYER,
} from 'constants/shared';
import { useGetAgreementOption } from 'components/contactFormCustomFields/agreementOptionToProcessPD.hooks';

export interface INotificationText {
    titleText: string;
    descText: string;
}

const langOptions = [
    {
        key: 'fr',
        label: 'Français',
    },
    { key: 'en', label: 'English' },
];

const ContactForm = () => {
    const { t } = useTranslation();
    const contactFormSchema = useContactFormSchema();

    const [step, setStep] = useState(1);

    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isMessageSentSuccessfully, setIsMessageSentSuccessfully] =
        useState(false);

    const notificationTextData = useGetNotificationTextData();

    const [notificationText, setNotificationText] = useState<INotificationText>(
        notificationTextData.success
    );

    const eventOptions = useGetEventOptions();
    // const budgetOptions = useGetBudgetOptions();
    const addServicesOptions = useGetAddServicesOptions();
    const agreementOption = useGetAgreementOption();

    emailjs.init(EMAIL_PUBLIC_KEY);

    const sendEmail = (
        formData: TFormData,
        setSubmitting: (_isSubmitting: boolean) => void
    ) => {
        emailjs
            .send(
                EMAIL_SERVICE_ID,
                EMAIL_TEMPLATE_ID_CALCULATION,
                formData,
                EMAIL_PUBLIC_KEY
            )
            .then(() => {
                setNotificationText(notificationTextData.success);
                setIsNotificationOpen(!isNotificationOpen);
                setIsMessageSentSuccessfully(true);

                WINDOW_DATALAYER.push({ event: 'ExtendedFormSent' });
            })
            .catch(() => {
                setNotificationText(notificationTextData.error);
                setIsNotificationOpen(!isNotificationOpen);
                setIsMessageSentSuccessfully(false);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const onSubmit = (
        values: TFormData,
        {
            setSubmitting,
            resetForm,
        }: {
            setSubmitting: (_isSubmitting: boolean) => void;
            resetForm: () => void;
        }
    ) => {
        setSubmitting(true);

        const formattedDate = new Date(values.date).toLocaleDateString(
            'en-US',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }
        );
        sendEmail(
            {
                ...values,
                date: formattedDate,
                addServices: values.addServices?.length
                    ? values.addServices
                    : ['No additional services'],
            },
            () => {
                setSubmitting(false);
                resetForm();
            }
        );

        resetForm();
    };

    const getStatusIcon = isMessageSentSuccessfully ? (
        <StatusOkIcon />
    ) : (
        <StatusErrorIcon />
    );

    return (
        <div className={styles.formContainer}>
            <h5 className={styles.formTitle}>{t('contactForm.title')}</h5>
            <Formik
                initialValues={{
                    name: '',
                    eventType: undefined,
                    date: '',
                    addServices: [],
                    email: '',
                    preferredLang: '',
                    agreementToProcessPD: '',
                }}
                validationSchema={contactFormSchema}
                onSubmit={onSubmit}
                validateOnBlur={true}
            >
                {({ isSubmitting, values }) => (
                    <Form className={styles.form}>
                        <div className={styles.inputGroup}>
                            <CustomField
                                label={t('contactFormBookEvent.firstName')}
                                id="name"
                                name="name"
                                type="text"
                                component="input"
                            />

                            <ErrorMessage
                                name="name"
                                component="div"
                                className={styles.error}
                            />
                        </div>
                        <div>
                            <CustomField
                                name="eventType"
                                label={t('contactForm.whatEventQuestion')}
                                component="checkboxList"
                                options={eventOptions}
                            />

                            <ErrorMessage
                                name="eventType"
                                component="div"
                                className={styles.error}
                            />
                        </div>

                        {step === 1 && (
                            <ContactFormFooter
                                step={step}
                                values={values}
                                setStep={setStep}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        {step >= 2 && (
                            <>
                                <div>
                                    <CustomField
                                        name="date"
                                        label={t(
                                            'contactForm.selectTheDateOfTheEvent'
                                        )}
                                        component="datepicker"
                                    />
                                    <ErrorMessage
                                        name="date"
                                        component="div"
                                        className={styles.error}
                                    />
                                </div>
                                {/*<div>*/}
                                {/*    <CustomField*/}
                                {/*        name="budget"*/}
                                {/*        label={t(*/}
                                {/*            'contactForm.whatBudgetQuestion'*/}
                                {/*        )}*/}
                                {/*        component="checkboxList"*/}
                                {/*        options={budgetOptions}*/}
                                {/*    />*/}

                                {/*    <ErrorMessage*/}
                                {/*        name="budget"*/}
                                {/*        component="div"*/}
                                {/*        className={styles.error}*/}
                                {/*    />*/}
                                {/*</div>*/}
                            </>
                        )}

                        {step === 2 && (
                            <ContactFormFooter
                                step={step}
                                values={values}
                                setStep={setStep}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        {step === 3 && (
                            <>
                                <div>
                                    <CustomField
                                        name="addServices"
                                        label={t(
                                            'contactForm.addServicesQuestion'
                                        )}
                                        component="checkboxList"
                                        options={addServicesOptions}
                                        multiSelect={true}
                                    />

                                    <ErrorMessage
                                        name="addServices"
                                        component="div"
                                        className={styles.error}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <CustomField
                                        label={t('contactForm.email')}
                                        id="email"
                                        name="email"
                                        component="input"
                                    />

                                    <ErrorMessage
                                        name="email"
                                        component="div"
                                        className={styles.error}
                                    />
                                </div>

                                <div>
                                    <CustomField
                                        name="preferredLang"
                                        label={t('contactForm.preferredLang')}
                                        component="checkboxList"
                                        options={langOptions}
                                    />

                                    <ErrorMessage
                                        name="preferredLang"
                                        component="div"
                                        className={styles.error}
                                    />
                                </div>

                                <div style={{ position: 'relative' }}>
                                    <CustomField
                                        name="agreementToProcessPD"
                                        label=""
                                        component="checkboxList"
                                        options={agreementOption}
                                    />

                                    <ErrorMessage
                                        name="agreementToProcessPD"
                                        component="div"
                                        className={styles.error}
                                    />
                                </div>

                                <ContactFormFooter
                                    step={step}
                                    values={values}
                                    setStep={setStep}
                                    isSubmitting={isSubmitting}
                                />
                            </>
                        )}
                    </Form>
                )}
            </Formik>

            <Notification
                isNotificationOpen={isNotificationOpen}
                setIsNotificationOpen={setIsNotificationOpen}
                icon={getStatusIcon}
                notificationText={notificationText}
            />
        </div>
    );
};

export default ContactForm;
